import { useState } from 'react'
import { formatIban } from '@core/validators/iban'
import { formatCents } from '@core/money'

/**
 * Sequential clipboard staging.
 *
 * The single-device problem: you cannot scan a QR on the same screen it is
 * displayed on, and pasting three fields into a banking app means three
 * app-switches. With separate copy buttons you come back and have to remember
 * which one you already used — and pasting the reference into the amount field
 * is a real, quiet way to pay the wrong thing.
 *
 * So there is one button, and it advances. Come back from the bank, tap, go
 * again. The step survives the app-switch because it lives in component state
 * and iOS restores the view.
 */
const STEP_LABELS = ['Kopieer rekeningnummer', 'Kopieer mededeling', 'Kopieer bedrag'] as const

export function CopySteps({
  iban,
  reference,
  amountCents,
}: {
  iban: string
  reference: string | null
  amountCents: number
}) {
  const steps = [
    { label: STEP_LABELS[0], value: iban, display: formatIban(iban) },
    ...(reference
      ? [{ label: STEP_LABELS[1], value: reference, display: reference }]
      : []),
    { label: STEP_LABELS[2], value: (amountCents / 100).toFixed(2), display: formatCents(amountCents) },
  ]

  const [step, setStep] = useState(0)
  const [copied, setCopied] = useState(false)
  const done = step >= steps.length

  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value)
    } catch {
      // Clipboard can be refused when the page is not focused. The value is
      // shown beside the button, so it stays usable either way.
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 1200)
    setStep((s) => s + 1)
  }

  return (
    <div className="space-y-2">
      {steps.map((s, i) => (
        <div
          key={s.label}
          className={`flex items-center gap-3 px-3 py-2 rounded-xl border ${
            i === step ? 'border-ink bg-surface' : 'border-line'
          }`}
        >
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted">{s.label.replace('Kopieer ', '')}</p>
            <p className="font-medium truncate tabular-nums">{s.display}</p>
          </div>
          <button
            onClick={() => copy(s.value)}
            className={`shrink-0 px-3 py-2 rounded-lg text-sm font-medium ${
              i === step ? 'bg-ink text-canvas' : 'bg-canvas text-muted border border-line'
            }`}
          >
            {i < step ? '✓' : 'Kopieer'}
          </button>
        </div>
      ))}

      {copied && <p className="text-sm text-ok text-center">Gekopieerd</p>}

      {done && (
        <button onClick={() => setStep(0)} className="w-full py-2 text-sm text-muted">
          Opnieuw beginnen
        </button>
      )}
    </div>
  )
}
